import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { AlertCircle, ArrowLeft, Loader2, Search } from 'lucide-react';
import { useClaimRejections, useRewardLog } from '../hooks';
import { RewardLogTable } from '../components/RewardLogTable';
import { RejectionTable } from '../components/RejectionTable';

type Outcome = 'success' | 'failure';

interface TimeRange {
  label: string;
  hours: number | undefined;
}

// Time filter ranges. `undefined` = no constraint (= 720h cap on the server,
// which is the maximum supported window).
const TIME_RANGES: TimeRange[] = [
  { label: 'Last hour', hours: 1 },
  { label: 'Last 24 hours', hours: 24 },
  { label: 'Last 7 days', hours: 24 * 7 },
  { label: 'Last 30 days', hours: 24 * 30 },
];

const DEFAULT_RANGE_HOURS = 24 * 7;

export function AdminRewardLogPage() {
  const [outcome, setOutcome] = useState<Outcome>('success');
  const [sinceHours, setSinceHours] = useState<number>(DEFAULT_RANGE_HOURS);
  const [usernameInput, setUsernameInput] = useState('');
  const [gameInput, setGameInput] = useState('');
  const [filterUsername, setFilterUsername] = useState('');
  const [filterGameConfigId, setFilterGameConfigId] = useState('');

  const handleFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setFilterUsername(usernameInput.trim());
    setFilterGameConfigId(gameInput.trim());
  };

  const handleClearFilters = () => {
    setUsernameInput('');
    setGameInput('');
    setFilterUsername('');
    setFilterGameConfigId('');
  };

  const hasActiveFilters = filterUsername || filterGameConfigId;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards/admin"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Admin
        </Link>
        <h1 className="text-2xl font-semibold">Reward Audit Log</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Successful claims live in <code>gr_reward_log</code>; rejections live in{' '}
          <code>gr_claim_rejection_log</code>. Use the tabs below to switch between them.
        </p>
      </div>

      <Tabs value={outcome} onValueChange={(v) => setOutcome(v as Outcome)}>
        <TabsList>
          <TabsTrigger value="success">Successful</TabsTrigger>
          <TabsTrigger value="failure">Rejected</TabsTrigger>
        </TabsList>

        {/* Filters live outside TabsContent so they don't unmount on tab
            switch — preserves typed-but-unsubmitted filter values. */}
        <form
          onSubmit={handleFilter}
          className="flex flex-wrap gap-2 items-end mt-4"
          aria-label="Audit log filters"
        >
          <div className="flex flex-col gap-1 min-w-[180px]">
            <label htmlFor="time-range" className="text-xs text-muted-foreground">
              Time range
            </label>
            <Select value={String(sinceHours)} onValueChange={(v) => setSinceHours(Number(v))}>
              <SelectTrigger id="time-range" aria-label="Time range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_RANGES.map((r) => (
                  <SelectItem key={r.label} value={String(r.hours)}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Filter by username..."
              value={usernameInput}
              onChange={(e) => setUsernameInput(e.target.value)}
              className="pl-9"
              aria-label="Filter by username"
              maxLength={64}
            />
          </div>
          {outcome === 'success' && (
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter by game config ID..."
                value={gameInput}
                onChange={(e) => setGameInput(e.target.value)}
                className="pl-9"
                aria-label="Filter by game config ID"
                maxLength={64}
              />
            </div>
          )}
          <Button type="submit" variant="secondary">
            Filter
          </Button>
          {hasActiveFilters && (
            <Button type="button" variant="ghost" onClick={handleClearFilters}>
              Clear
            </Button>
          )}
        </form>

        <TabsContent value="success" className="mt-4">
          <SuccessfulView
            username={filterUsername}
            gameConfigId={filterGameConfigId}
            sinceHours={sinceHours}
          />
        </TabsContent>
        <TabsContent value="failure" className="mt-4">
          <RejectedView username={filterUsername} sinceHours={sinceHours} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============================================================================
// Successful claims view (gr_reward_log)
// ============================================================================

function SuccessfulView({
  username,
  gameConfigId,
  sinceHours,
}: {
  username: string;
  gameConfigId: string;
  sinceHours: number;
}) {
  const { entries, total, loading, error, loadMore, hasMore } = useRewardLog({
    username: username || undefined,
    gameConfigId: gameConfigId || undefined,
    sinceHours,
  });

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
        <AlertCircle className="h-4 w-4 text-destructive" />
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (loading && entries.length === 0) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 rounded-lg" />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-12">
        No successful claims match the current filters.
      </p>
    );
  }

  return (
    <>
      <div className="text-sm text-muted-foreground">
        {total} successful {total === 1 ? 'claim' : 'claims'}
      </div>
      <RewardLogTable entries={entries} showUser />
      {hasMore && (
        <div className="text-center">
          <Button variant="outline" onClick={loadMore} disabled={loading}>
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Load more
          </Button>
        </div>
      )}
    </>
  );
}

// ============================================================================
// Rejected claims view (gr_claim_rejection_log)
// ============================================================================

function RejectedView({ username, sinceHours }: { username: string; sinceHours: number }) {
  const { rejections, total, loading, error } = useClaimRejections({
    username: username || undefined,
    sinceHours,
    limit: 100,
  });

  if (error) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
        <AlertCircle className="h-4 w-4 text-destructive" />
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  if (loading && rejections.length === 0) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 rounded-lg" />
        ))}
      </div>
    );
  }

  if (rejections.length === 0) {
    return (
      <p className="text-center text-muted-foreground py-12">
        No rejected claims match the current filters.
      </p>
    );
  }

  return (
    <>
      <div className="text-sm text-muted-foreground">
        {total} rejected {total === 1 ? 'claim' : 'claims'}
      </div>
      <RejectionTable entries={rejections} />
    </>
  );
}
